/**
 * 19. 覆い焼き・焼き込みの地図（シート）
 *
 * モノクロ写真の上を指でなぞって範囲を描き、範囲ごとに
 *   種類（焼き込み／覆い焼き）・秒数・メモ
 * を付ける。Shoot.printing.dodgeBurnMap に保存する。
 *   [{ id, type: 'burn' | 'dodge', sec, note, points: [[x, y], ...] }]  座標は写真内の 0〜1
 * 文字の「覆い焼き・焼き込みメモ」（dodgeBurnNotes）とは別に持ち、既存の記録は変えない。
 */

import { openSheet, escapeHtml, toast, buzz, icon, blobUrl, releaseUrls, confirmDialog } from './ui.js';
import { getCurrentShoot, updateCurrentShoot } from './scene.js';

const MIN_POINTS = 6;

export function dodgeBurnSummary(regions) {
  return (regions || []).map((r, i) => `${i + 1}. ${r.note || '範囲'} ${r.type === 'burn' ? '焼き込み +' : '覆い焼き −'}${r.sec} 秒`).join(' / ');
}

export function openDodgeBurnMap(onChange) {
  const shoot = getCurrentShoot();
  if (!shoot) return;
  let regions = JSON.parse(JSON.stringify(shoot.printing?.dodgeBurnMap || []));
  let selected = null;
  let tool = 'burn';

  const body = document.createElement('div');
  body.innerHTML = `
    <div style="padding:0 16px 10px;display:flex;gap:8px;align-items:center">
      <div class="seg" style="flex:1" id="db-tool">
        <button type="button" class="seg__opt" data-tool="burn">焼き込み（＋）</button>
        <button type="button" class="seg__opt" data-tool="dodge">覆い焼き（−）</button>
      </div>
    </div>
    <div style="display:flex;justify-content:center;padding:0 16px">
      <div class="photo" id="db-photo" style="position:relative;display:inline-block;touch-action:none;max-width:100%">
        <img id="db-img" src="${blobUrl('dodge', shoot.monoImageBlob || shoot.croppedImageBlob)}" alt="" draggable="false" style="display:block;max-width:100%;max-height:52vh;width:auto;height:auto" />
        <svg id="db-svg" style="position:absolute;inset:0;width:100%;height:100%;overflow:visible;z-index:2" preserveAspectRatio="none"></svg>
      </div>
    </div>
    <p class="sub" style="font-size:12px;line-height:1.6;margin:8px 24px 0">写真の上を指でなぞって範囲を囲みます。範囲をタップすると選べます。</p>
    <div id="db-edit" style="padding:0 24px"></div>
    <div id="db-list"></div>
    <div style="height:24px"></div>`;
  const sheet = openSheet({
    en: 'dodge & burn', title: '覆い焼き・焼き込みの地図', body, full: true,
    onClose: () => { releaseUrls('dodge'); onChange?.(); },
  });

  const $ = (q) => body.querySelector(q);
  const img = $('#db-img');
  const svg = $('#db-svg');

  const persist = () => {
    const p = getCurrentShoot().printing || {};
    updateCurrentShoot({ printing: { ...p, dodgeBurnMap: regions } });
  };

  function size() {
    const r = img.getBoundingClientRect();
    return { w: r.width || 1, h: r.height || 1, left: r.left, top: r.top };
  }
  function centroid(pts) {
    let x = 0; let y = 0;
    pts.forEach(([a, b]) => { x += a; y += b; });
    return [x / pts.length, y / pts.length];
  }
  function paintTool() {
    body.querySelectorAll('[data-tool]').forEach((b) => b.classList.toggle('is-on', b.dataset.tool === tool));
  }

  function draw(drawing = null) {
    const { w, h } = size();
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    const poly = (pts) => pts.map(([x, y]) => `${(x * w).toFixed(1)},${(y * h).toFixed(1)}`).join(' ');
    svg.innerHTML = regions.map((r, i) => {
      const [cx, cy] = centroid(r.points);
      const burn = r.type === 'burn';
      const sel = r.id === selected;
      return `<g data-region="${escapeHtml(r.id)}" style="cursor:pointer">
        <polygon points="${poly(r.points)}" style="fill:${burn ? 'var(--zone-0)' : 'var(--zone-10)'};fill-opacity:${burn ? '.35' : '.4'};stroke:${burn ? 'var(--zone-0)' : 'var(--zone-10)'}" stroke-width="${sel ? 3 : 1.5}" ${burn ? '' : 'stroke-dasharray="6 4"'} />
        <g transform="translate(${(cx * w).toFixed(1)},${(cy * h).toFixed(1)})">
          <rect x="-28" y="-12" width="56" height="24" style="fill:${burn ? 'var(--color-solid)' : 'var(--color-paper)'};stroke:var(--color-ink)" stroke-width="${sel ? 2 : 1}" />
          <text x="0" y="5" text-anchor="middle" font-family="Inter Tight, sans-serif" font-size="12" font-weight="600" style="fill:${burn ? 'var(--color-on-solid)' : 'var(--color-ink)'}">${i + 1} ${burn ? '+' : '−'}${r.sec}s</text>
        </g>
      </g>`;
    }).join('') + (drawing ? `<polyline points="${poly(drawing)}" fill="none" style="stroke:${tool === 'burn' ? 'var(--zone-0)' : 'var(--zone-10)'}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round" />` : '');
  }

  function paintEdit() {
    const box = $('#db-edit');
    const r = regions.find((x) => x.id === selected);
    if (!r) { box.innerHTML = ''; return; }
    const i = regions.indexOf(r);
    box.innerHTML = `
      <div style="margin-top:12px;padding:12px 14px;box-shadow:inset 0 0 0 1.5px var(--color-ink)">
        <div style="display:flex;justify-content:space-between;align-items:center"><span class="field__label" style="margin:0">範囲 ${i + 1}</span><button type="button" class="bar__btn" data-del aria-label="この範囲を消す" style="width:36px;height:36px">${icon('trash', 18)}</button></div>
        <div class="seg seg--md" style="margin-top:6px"><button type="button" class="seg__opt ${r.type === 'burn' ? 'is-on' : ''}" data-type="burn">焼き込み（＋）</button><button type="button" class="seg__opt ${r.type === 'dodge' ? 'is-on' : ''}" data-type="dodge">覆い焼き（−）</button></div>
        <div style="display:flex;align-items:center;gap:8px;margin-top:10px">
          <span class="field__label" style="margin:0;width:44px">秒数</span>
          <button type="button" class="cond__pm" data-sec="-1" aria-label="1 秒減らす">−</button>
          <span class="num" style="flex:1;text-align:center;font:400 22px/1 var(--font-en)">${r.type === 'burn' ? '+' : '−'}${r.sec}<small style="font-size:13px"> 秒</small></span>
          <button type="button" class="cond__pm" data-sec="1" aria-label="1 秒増やす">+</button>
        </div>
        <label class="field" style="margin-top:10px"><span class="field__label">メモ（範囲の名前）</span><input class="input" data-note value="${escapeHtml(r.note || '')}" placeholder="例：空、左下の岩、顔" /></label>
      </div>`;
    box.querySelectorAll('[data-type]').forEach((b) => b.addEventListener('click', () => { r.type = b.dataset.type; persist(); refresh(); }));
    box.querySelectorAll('[data-sec]').forEach((b) => b.addEventListener('click', () => {
      r.sec = Math.max(0.5, Math.min(300, Math.round((r.sec + Number(b.dataset.sec)) * 2) / 2));
      buzz(4);
      persist();
      refresh();
    }));
    box.querySelector('[data-note]').addEventListener('input', (e) => { r.note = e.target.value.trim().slice(0, 40); persist(); draw(); paintList(); });
    box.querySelector('[data-del]').addEventListener('click', async () => {
      const ok = await confirmDialog({ title: `範囲 ${i + 1} を消しますか？`, ok: '消す' });
      if (!ok) return;
      regions = regions.filter((x) => x.id !== r.id);
      selected = null;
      persist();
      refresh();
    });
  }

  function paintList() {
    const box = $('#db-list');
    if (!regions.length) { box.innerHTML = ''; return; }
    box.innerHTML = `
      <div class="spots-head" style="margin-top:8px"><span class="eyebrow">regions · ${regions.length}</span><span class="sub" style="font:400 12px/1 var(--font-jp-sans)">焼き込み 計 +${regions.filter((r) => r.type === 'burn').reduce((a, r) => a + r.sec, 0)} 秒</span></div>
      ${regions.map((r, i) => `
        <button type="button" class="spot-row" data-pick="${escapeHtml(r.id)}" style="${r.id === selected ? 'background:var(--color-grain)' : ''}">
          <span class="spot-row__n ${r.type === 'burn' ? 'is-anchor' : ''}">${i + 1}</span>
          <div class="spot-row__body"><div class="spot-row__label">${escapeHtml(r.note || '名前なし')}</div><div class="spot-row__role">${r.type === 'burn' ? '焼き込み' : '覆い焼き'}</div></div>
          <span class="spot-row__ev">${r.type === 'burn' ? '+' : '−'}${r.sec}s</span>
        </button>`).join('')}`;
    box.querySelectorAll('[data-pick]').forEach((b) => b.addEventListener('click', () => { selected = b.dataset.pick; refresh(); }));
  }

  function refresh() { draw(); paintEdit(); paintList(); }

  // なぞって範囲を描く
  const photo = $('#db-photo');
  let pts = null;
  photo.addEventListener('pointerdown', (e) => {
    const hit = e.target.closest('[data-region]');
    if (hit) { selected = hit.dataset.region; buzz(6); refresh(); return; }
    try { photo.setPointerCapture(e.pointerId); } catch {}
    const s = size();
    pts = [[(e.clientX - s.left) / s.w, (e.clientY - s.top) / s.h]];
  });
  photo.addEventListener('pointermove', (e) => {
    if (!pts) return;
    const s = size();
    const p = [Math.max(0, Math.min(1, (e.clientX - s.left) / s.w)), Math.max(0, Math.min(1, (e.clientY - s.top) / s.h))];
    const last = pts[pts.length - 1];
    if (Math.hypot(p[0] - last[0], p[1] - last[1]) > 0.008) { pts.push(p); draw(pts); }
  });
  const end = () => {
    if (!pts) return;
    const done = pts;
    pts = null;
    if (done.length < MIN_POINTS) { draw(); return; }
    const r = { id: `db_${Date.now().toString(36)}`, type: tool, sec: tool === 'burn' ? 4 : 2, note: '', points: done.map(([x, y]) => [Math.round(x * 1000) / 1000, Math.round(y * 1000) / 1000]) };
    regions.push(r);
    selected = r.id;
    buzz(10);
    persist();
    refresh();
    toast('範囲を追加しました。秒数とメモを入れてください');
  };
  photo.addEventListener('pointerup', end);
  photo.addEventListener('pointercancel', end);

  body.querySelectorAll('[data-tool]').forEach((b) => b.addEventListener('click', () => { tool = b.dataset.tool; buzz(6); paintTool(); }));
  img.addEventListener('load', () => refresh());
  window.addEventListener('resize', () => draw(), { passive: true });
  paintTool();
  refresh();
  void sheet;
}
